import React from 'react';

type StepStatus = 'processing' | 'inService' | 'inProduct' | 'completed';

interface StepperProps {
  currentStep: StepStatus;
  type: 'service' | 'product';
}

const ServiceStepOrder: StepStatus[] = ['processing', 'inService', 'completed'];
const ProductStepOrder: StepStatus[] = ['processing', 'inProduct', 'completed'];

const ServiceStepLabels: Record<StepStatus, string> = {
  processing: 'Service Processing Started',
  inService: 'In Service',
  inProduct: '',
  completed: 'Service Completed',
};

const ProductStepLabels: Record<StepStatus, string> = {
  processing: 'Product Processing Started',
  inService: '',
  inProduct: 'In Products & Packaging',
  completed: 'Product & Packaging Completed',
};

const getIcon = (status: 'completed' | 'active' | 'upcoming') => {
  if (status === 'completed') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 0C3.584 0 0 3.584 0 8C0 12.416 3.584 16 8 16C12.416 16 16 12.416 16 8C16 3.584 12.416 0 8 0ZM5.832 11.432L2.96 8.56C2.648 8.248 2.648 7.744 2.96 7.432C3.272 7.12 3.776 7.12 4.088 7.432L6.4 9.736L11.904 4.232C12.216 3.92 12.72 3.92 13.032 4.232C13.344 4.544 13.344 5.048 13.032 5.36L6.96 11.432C6.656 11.744 6.144 11.744 5.832 11.432Z"
          fill="#000000B2"
        />
      </svg>
    );
  }
  if (status === 'active') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 12C9.10667 12 10.05 11.61 10.83 10.83C11.61 10.05 12 9.10667 12 8C12 6.89333 11.61 5.95 10.83 5.17C10.05 4.39 9.10667 4 8 4C6.89333 4 5.95 4.39 5.17 5.17C4.39 5.95 4 6.89333 4 8C4 9.10667 4.39 10.05 5.17 10.83C5.95 11.61 6.89333 12 8 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16ZM8 14.4C9.78667 14.4 11.3 13.78 12.54 12.54C13.78 11.3 14.4 9.78667 14.4 8C14.4 6.21333 13.78 4.7 12.54 3.46C11.3 2.22 9.78667 1.6 8 1.6C6.21333 1.6 4.7 2.22 3.46 3.46C2.22 4.7 1.6 6.21333 1.6 8C1.6 9.78667 2.22 11.3 3.46 12.54C4.7 13.78 6.21333 14.4 8 14.4Z"
          fill="#000000B2"
        />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect
        x="1"
        y="1"
        width="14"
        height="14"
        rx="7"
        stroke="#D1D1D6"
        strokeWidth="2"
      />
    </svg>
  );
};

export const ServiceTaskProgress: React.FC<StepperProps> = ({
  currentStep,
  type,
}) => {
  const stepOrder = type === 'product' ? ProductStepOrder : ServiceStepOrder;
  const labels = type === 'product' ? ProductStepLabels : ServiceStepLabels;

  const currentIndex = stepOrder.indexOf(currentStep);

  return (
    <div className="relative flex items-center justify-between bg-[#DFDFD7] rounded-md shadow py-4 px-2">
      {stepOrder.map((step, index) => {
        // Mark the step as 'completed' if it's before the current index,
        // or if it's the final 'completed' step and the current step is 'completed'.
        // Otherwise, mark it as 'active' if it's the current step, or 'upcoming' if not yet reached.
        const isFinalStep = step === 'completed';
        const status =
          index < currentIndex || (isFinalStep && currentStep === 'completed')
            ? 'completed'
            : index === currentIndex
              ? 'active'
              : 'upcoming';

        return (
          <React.Fragment key={step}>
            {index > 0 && (
              <div
                className={`absolute top-[28px] h-0.5 bg-[#BBBECB] z-0`}
                style={{
                  left: `${index * 33.33 - 16.665}%`,
                  right: `${100 - index * 33.33 - 16.665}%`,
                }}
              />
            )}
            <div className="relative z-10 flex flex-col items-center text-center flex-2">
              <div className="w-7 h-7 flex items-center justify-center bg-DFDFD7">
                {getIcon(status)}
              </div>
              {labels[step] && (
                <span
                  className={`mt-2 text-xs ${
                    status === 'completed' || status === 'active'
                      ? 'text-black'
                      : 'text-gray-400'
                  }`}
                >
                  {labels[step]}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
